/**
 * Screenlux Exit-Intent Pre-Purchase Survey
 *
 * One-tap "why aren't you buying?" popup — records the reason for analytics, then closes.
 * Each tap routes the visitor to the thing that removes the blocker and is
 * quietly recorded to the Powerups app (/apps/measurements?action=submit-survey).
 *
 * Design rules (deliberate):
 *  - Eligible only on product/configurator + cart pages.
 *  - Never on a cold first pageview — only after intent is shown
 *    (scrolled the PDP, added to cart, or already on the cart page).
 *  - Once per session, and never re-opens once seen or closed.
 *  - Waits until the cookie-consent decision is resolved (no modal stacking).
 *  - Triggers: desktop = mouse-toward-close exit intent OR ~35s inactivity;
 *    mobile = fast scroll-up flick OR ~35s inactivity.
 *  - Append ?slxsurvey=test to any eligible URL to force it open (for QA).
 */
(function () {
  'use strict';

  var cfg = window.ExitSurveyConfig || {};
  var ELIGIBLE_PAGES = ['product', 'cart'];
  var SEEN_KEY = 'slx_survey_seen';
  var INTENT_KEY = 'slx_intent';
  var SID_KEY = 'slx_sid';
  var IDLE_MS = 35000;
  var ARM_DELAY_MS = 3000; // don't arm triggers immediately on load

  if (ELIGIBLE_PAGES.indexOf(cfg.pageType) === -1) return;

  var el = document.getElementById('slx-survey');
  if (!el) return;

  var forceTest = /[?&]slxsurvey=(test|1)\b/.test(location.search);
  var isTouch = window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  var shown = false;
  var armed = false;
  var idleTimer = null;

  /* ── session helpers ─────────────────────────────────────────────── */
  function ss(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } }
  function ssSet(key, val) { try { sessionStorage.setItem(key, val); } catch (e) {} }

  function sessionId() {
    var id = ss(SID_KEY);
    if (!id) {
      id = 's' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      ssSet(SID_KEY, id);
    }
    return id;
  }
  var sid = sessionId();

  function seen() { return ss(SEEN_KEY) === '1'; }
  function markSeen() { ssSet(SEEN_KEY, '1'); }
  function hasIntent() { return ss(INTENT_KEY) === '1'; }
  function markIntent() { ssSet(INTENT_KEY, '1'); }

  /* ── consent gating ──────────────────────────────────────────────── */
  // Check currentVisitorConsent() FIRST: on this store shouldShowBanner() can
  // stay true even after a visitor has already answered (marketing/analytics/
  // preferences all 'yes'), which would block the popup forever. An explicit
  // 'yes' or 'no' on any category means they've genuinely engaged with the
  // banner already, so that takes priority. Only fall back to shouldShowBanner
  // when there's no real consent decision to read yet.
  function consentResolved() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return true; // no privacy API on the page → nothing to stack with
      if (typeof cp.currentVisitorConsent === 'function') {
        var c = cp.currentVisitorConsent() || {};
        var answered = [c.marketing, c.analytics, c.preferences, c.sale_of_data].some(function (v) {
          return v === 'yes' || v === 'no';
        });
        if (answered) return true;
      }
      if (typeof cp.shouldShowBanner === 'function') return !cp.shouldShowBanner();
      return true;
    } catch (e) { return true; }
  }

  /* ── intent detection ────────────────────────────────────────────── */
  if (cfg.pageType === 'cart') {
    markIntent(); // being on the cart is intent by definition
  }

  function onScrollDepth() {
    var doc = document.documentElement;
    var reached = (window.scrollY + window.innerHeight) / (doc.scrollHeight || 1);
    if (reached > 0.4) {
      markIntent();
      window.removeEventListener('scroll', onScrollDepth);
    }
  }
  window.addEventListener('scroll', onScrollDepth, { passive: true });

  // Add-to-cart is strong intent.
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.getAttribute && (f.getAttribute('action') || '').indexOf('/cart/add') !== -1) markIntent();
  }, true);
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('[name="add"], .product-form__submit')) markIntent();
  }, true);

  /* ── the popup ───────────────────────────────────────────────────── */
  function open(force) {
    if (shown) return;
    if (!force && seen()) return;
    if (!force && !consentResolved()) return; // skip this attempt; a later trigger will retry
    shown = true;
    markSeen();
    el.style.display = 'flex';
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    var closeBtn = el.querySelector('.sl-modal-close');
    if (closeBtn) closeBtn.focus();
    teardownTriggers();
  }

  function close() {
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  /* ── network + tracking ──────────────────────────────────────────── */
  // The campaign that brought this visitor, captured on the landing page by
  // attribution.js. location.href almost never still carries it by the time a
  // form is submitted.
  function attribution() {
    try { return window.slxAttribution ? window.slxAttribution.get() : {}; }
    catch (e) { return {}; }
  }
  // Long-lived first/last touch (90 days) from attribution.js, or null.
  function attrTouches() {
    try { return window.slxAttribution && window.slxAttribution.attr ? window.slxAttribution.attr() : null; }
    catch (e) { return null; }
  }


  function post(extra) {
    var payload = {
      reason: '', comment: '', email: '',
      pageUrl: location.href,
      utm: attribution(),
      attr: attrTouches(),
      pageType: cfg.pageType || '',
      market: cfg.market || '',
      device: isTouch ? 'mobile' : 'desktop',
      sessionId: sid
    };
    for (var k in extra) { if (extra.hasOwnProperty(k)) payload[k] = extra[k]; }
    try {
      fetch((cfg.proxyBase || '/apps/measurements') + '?action=submit-survey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true // survive navigation when they click a CTA
      }).catch(function () {});
    } catch (e) {}
  }

  function trackGA(reason) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'exit_survey_response', {
          reason: reason,
          page_type: cfg.pageType || '',
          market: cfg.market || ''
        });
      }
    } catch (e) {}
  }

  /* ── record reason, then thank & close ───────────────────────────── */
  var optionsStep = el.querySelector('[data-slx-step="options"]');
  var answerStep = el.querySelector('[data-slx-step="answer"]');
  var thanksBox = el.querySelector('[data-slx-thanks]');
  var thanksText = el.querySelector('[data-slx-thanks-text]');
  var subtitleEl = el.querySelector('.sl-modal-sub');

  // The survey is purely for data gathering: swap the options for a brief
  // thank-you and auto-close, without persuading or asking for anything more.
  function showThanks(text) {
    if (optionsStep) optionsStep.hidden = true;
    if (subtitleEl) subtitleEl.hidden = true;
    if (answerStep) answerStep.hidden = false;
    if (thanksText && text) thanksText.textContent = text;
    if (thanksBox) thanksBox.hidden = false;
    setTimeout(close, 1800);
  }

  // Option taps: record the reason (backend + GA), then thank and close.
  var opts = el.querySelectorAll('.slx-survey-opt');
  for (var i = 0; i < opts.length; i++) {
    opts[i].addEventListener('click', function () {
      var reason = this.getAttribute('data-reason');
      post({ reason: reason });
      trackGA(reason);
      showThanks();
    });
  }

  // Close interactions: X button, click on the backdrop, and Escape.
  var closeEls = el.querySelectorAll('[data-slx-close]');
  for (var c = 0; c < closeEls.length; c++) closeEls[c].addEventListener('click', close);
  el.addEventListener('click', function (e) { if (e.target === el) close(); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && shown) close();
  });

  /* ── triggers ────────────────────────────────────────────────────── */
  function tryOpen() {
    if (!hasIntent()) return;
    open(false);
  }

  function onMouseOut(e) {
    if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) tryOpen();
  }

  var lastY = window.scrollY;
  var lastT = (window.performance && performance.now) ? performance.now() : Date.now();
  function onScrollVelocity() {
    var y = window.scrollY;
    var now = (window.performance && performance.now) ? performance.now() : Date.now();
    var dy = lastY - y;          // positive = scrolling up
    var dt = now - lastT;
    lastY = y; lastT = now;
    if (dy > 140 && dt > 0 && (dy / dt) > 1.4 && y < 400) tryOpen(); // fast upward flick near top
  }

  function resetIdle() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(tryOpen, IDLE_MS);
  }

  var ACTIVITY_EVENTS = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];

  function setupTriggers() {
    if (armed) return;
    armed = true;
    // Idle fallback on every device, so it doesn't depend solely on the gesture.
    ACTIVITY_EVENTS.forEach(function (ev) { document.addEventListener(ev, resetIdle, { passive: true }); });
    resetIdle();
    if (isTouch) {
      window.addEventListener('scroll', onScrollVelocity, { passive: true });
    } else {
      document.addEventListener('mouseout', onMouseOut);
    }
  }

  function teardownTriggers() {
    if (idleTimer) clearTimeout(idleTimer);
    window.removeEventListener('scroll', onScrollVelocity);
    document.removeEventListener('mouseout', onMouseOut);
    ACTIVITY_EVENTS.forEach(function (ev) { document.removeEventListener(ev, resetIdle); });
  }

  if (forceTest) {
    markIntent();
    setTimeout(function () { open(true); }, 600);
  } else if (!seen()) {
    setTimeout(setupTriggers, ARM_DELAY_MS);
  }
})();
